import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Users, MapPin, X, SlidersHorizontal, Building2, Rss, Heart } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useRelationshipStatus } from "@/lib/relationshipStore";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loading, EmptyState } from "@/components/common/Loading";
import { MessageButton } from "@/components/social/ProfileActions";
import { DirectoryFilters, DIR_FILTERS } from "@/components/directory/DirectoryFilters";
import { isRegionCenter } from "@/components/directory/GeoRadiusFilter";
import { useReferences, labelFor } from "@/hooks/useReferences";

const emptyFilters = () => ({ multi: {}, sort: "recent", geoOn: false, center: null, radius: 50 });
const loc = (l) => [l?.city, l?.state].filter(Boolean).join(", ");
const orgHref = (a) => (a?.target_type === "capital" ? `/capital/${a.slug}` : `/b/${a.slug}`);

/** A single person row used inside every relationship modal (friends/followers/fans/mutual). */
function PersonResultCard({ it, references }) {
  return (
    <Card className="flex h-full flex-col border-0 p-4" data-testid={`people-card-${it.slug || it.user_id}`}>
      <div className="flex items-start gap-3">
        <Link to={`/p/${it.slug}`} className="shrink-0">
          <Avatar className="h-12 w-12">
            {it.headshot_url && <AvatarImage src={fileUrl(it.headshot_url)} className="object-cover" />}
            <AvatarFallback className="bg-teal-50 text-teal-700">{(it.display_name || "U")[0]}</AvatarFallback>
          </Avatar>
        </Link>
        <div className="min-w-0 flex-1">
          <Link to={`/p/${it.slug}`} className="block truncate font-[640] text-foreground hover:underline" data-testid={`people-name-${it.slug || it.user_id}`}>{it.display_name}</Link>
          {it.handle && <div className="truncate text-sm text-primary">@{it.handle}</div>}
        </div>
        <MessageButton targetUserId={it.user_id} variant="secondary" iconOnly />
      </div>

      {loc(it.location) && (
        <div className="mt-2.5 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5 shrink-0" /> {loc(it.location)}</div>
      )}
      {it.primary_affiliation && it.primary_affiliation.slug && (
        <Link to={orgHref(it.primary_affiliation)} className="mt-1.5 flex items-center gap-1.5 text-sm text-foreground hover:underline" data-testid={`people-affiliation-${it.slug || it.user_id}`}>
          <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> <span className="truncate">{it.primary_affiliation.display_name}</span>
        </Link>
      )}
      {(it.industries || []).length > 0 && (
        <div className="mt-auto flex flex-wrap gap-1.5 pt-3">
          {(it.industries || []).slice(0, 3).map((i) => <Badge key={i} variant="secondary" className="bg-secondary">{labelFor(references.industries, i)}</Badge>)}
          {(it.industries || []).length > 3 && <Badge variant="secondary" className="bg-secondary">+{(it.industries || []).length - 3}</Badge>}
        </div>
      )}
    </Card>
  );
}

/** Shared, searchable/filterable people list. `loadIds` is an async fn that resolves to
 *  an array of user_ids (the relationship scope). The body then enriches them via the
 *  People directory and renders the same UX as the Network friends search. */
export function PeopleListBody({ loadIds, noun = "person", nounPlural = "people" }) {
  const { references } = useReferences();
  const groups = useMemo(() => DIR_FILTERS.people, []);
  const [ids, setIds] = useState(null);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [items, setItems] = useState(null);

  const patch = useCallback((p) => setFilters((f) => ({ ...f, ...p })), []);
  const clearAll = useCallback(() => setFilters((f) => ({ multi: {}, sort: "recent", geoOn: false, center: f.center, radius: 50 })), []);

  useEffect(() => {
    let ok = true;
    Promise.resolve(loadIds())
      .then((arr) => { if (ok) setIds(arr || []); })
      .catch(() => { if (ok) setIds([]); });
    return () => { ok = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const multiKey = JSON.stringify(filters.multi);
  const centerKey = filters.center ? `${filters.center.lat},${filters.center.lng}` : "";
  const idsKey = (ids || []).join(",");

  useEffect(() => {
    if (ids === null) return undefined;
    if (ids.length === 0) { setItems([]); return undefined; }
    const controller = new AbortController();
    let cancelled = false;
    const params = { user_ids: idsKey, limit: 200 };
    if (dq) params.q = dq;
    groups.forEach((g) => { const arr = filters.multi[g.key]; if (arr && arr.length) params[g.key] = arr.join(","); });
    let sort = filters.sort;
    if (filters.geoOn && filters.center) {
      const c = filters.center;
      if (isRegionCenter(c)) {
        if (c.granularity === "state") params.state = c.state_code; else params.country = c.country_code;
      } else { params.lat = c.lat; params.lng = c.lng; params.radius = filters.radius; }
    } else if (sort === "nearest") { sort = "recent"; }
    params.sort = sort;
    setItems(null);
    (async () => {
      try {
        const { data } = await api.get("/directory/people", { params, signal: controller.signal });
        if (!cancelled) setItems(data);
      } catch (e) {
        if (!cancelled && e?.code !== "ERR_CANCELED" && e?.name !== "CanceledError") setItems([]);
      }
    })();
    return () => { cancelled = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, dq, multiKey, filters.sort, filters.geoOn, filters.radius, centerKey, ids]);

  const chips = useMemo(() => {
    const out = [];
    groups.forEach((g) => (filters.multi[g.key] || []).forEach((v) => out.push({ key: g.key, value: v, label: labelFor(references[g.ref], v) })));
    if (filters.geoOn && filters.center) out.push({ key: "__geo", value: "geo", label: isRegionCenter(filters.center) ? `In ${filters.center.label}` : `Within ${filters.radius} mi of ${filters.center.label}` });
    return out;
  }, [filters, groups, references]);

  const removeChip = (chip) => {
    if (chip.key === "__geo") { patch({ geoOn: false }); return; }
    patch({ multi: { ...filters.multi, [chip.key]: (filters.multi[chip.key] || []).filter((v) => v !== chip.value) } });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[180px] flex-1">
          <Input placeholder={`Search ${nounPlural}…`} value={q} onChange={(e) => setQ(e.target.value)} data-testid="people-list-search-input" />
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="secondary" data-testid="people-list-filter-trigger">
              <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Filters{chips.length > 0 && <Badge className="ml-2 bg-primary text-primary-foreground">{chips.length}</Badge>}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="max-h-[70vh] w-[min(92vw,420px)] overflow-y-auto p-0" data-testid="people-list-filter-popover">
            <DirectoryFilters kind="people" value={filters} onChange={patch} onClear={clearAll} hasActive={chips.length > 0} />
          </PopoverContent>
        </Popover>
        <Select value={filters.sort} onValueChange={(v) => patch({ sort: v })}>
          <SelectTrigger className="w-[150px]" data-testid="people-list-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Name (A–Z)</SelectItem>
            <SelectItem value="name_desc">Name (Z–A)</SelectItem>
            <SelectItem value="recent">Newest Added</SelectItem>
            <SelectItem value="nearest" disabled={!filters.center}>Nearest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {chips.map((c, i) => (
            <button key={`${c.key}-${c.value}-${i}`} onClick={() => removeChip(c)} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1 text-xs font-[500] text-foreground transition-colors hover:bg-secondary" data-testid={`people-list-chip-${c.value}`}>
              {c.label}<X className="h-3 w-3" />
            </button>
          ))}
          <button onClick={clearAll} className="text-xs font-[540] text-primary hover:underline">Clear all</button>
        </div>
      )}

      {items !== null && (
        <div className="mt-3 text-sm text-muted-foreground" data-testid="people-list-result-count">{items.length} {items.length === 1 ? noun : nounPlural}{chips.length > 0 || dq ? " match your search" : ""}</div>
      )}

      <div className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1" data-testid="people-list-scroll-area">
        {ids === null || items === null ? (
          <Loading />
        ) : items.length === 0 ? (
          <EmptyState icon={Users} title={`No ${nounPlural} to show`} description="Try removing a filter or widening your search." testId="people-list-empty" />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {items.map((it) => <PersonResultCard key={it.slug || it.user_id} it={it} references={references} />)}
          </div>
        )}
      </div>
    </div>
  );
}

/** Large, scrollable dialog wrapping {@link PeopleListBody}. */
export function PeopleListModal({ open, onOpenChange, title, count, loadIds, noun, nounPlural }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[85vh] max-h-[85vh] w-[95vw] max-w-[1100px] flex-col gap-4 sm:max-w-[1100px]" data-testid="people-list-modal">
        <DialogHeader>
          <DialogTitle>{title} {typeof count === "number" && <span className="ml-1 font-[440] text-muted-foreground">({count})</span>}</DialogTitle>
          <DialogDescription className="sr-only">A searchable, filterable list of {nounPlural || "people"}.</DialogDescription>
        </DialogHeader>
        {open && <PeopleListBody loadIds={loadIds} noun={noun} nounPlural={nounPlural} />}
      </DialogContent>
    </Dialog>
  );
}

/** Followers + fans counters for business/capital profiles. The numbers are published-only
 *  so they match the modal lists; the words open the Followers / Fans modals. */
export function OrgFollowCounts({ targetType, targetId, targetName, className = "" }) {
  const [counts, setCounts] = useState(null);
  // Subscribe to the shared relationship store so fan/follow counts update the
  // instant the viewer toggles Follow/Fan (the buttons use the same store).
  const rel = useRelationshipStatus(targetType, targetId);
  useEffect(() => {
    let ok = true;
    api.get("/network/relationship/counts", { params: { target_type: targetType, target_id: targetId } })
      .then(({ data }) => { if (ok) setCounts(data); })
      .catch(() => { if (ok) setCounts({ follower_count: 0, fan_count: 0 }); });
    return () => { ok = false; };
  }, [targetType, targetId]);
  if (!counts) return null;
  const who = targetName ? `${targetName}'s` : "Their";
  const followerCount = rel && typeof rel.follow_count === "number" ? rel.follow_count : counts.follower_count;
  const fanCount = rel && typeof rel.fan_count === "number" ? rel.fan_count : counts.fan_count;
  const followerIds = async () => (await api.get("/network/profile-followers", { params: { target_type: targetType, target_id: targetId, kind: "follow" } })).data.user_ids;
  const fanIds = async () => (await api.get("/network/profile-followers", { params: { target_type: targetType, target_id: targetId, kind: "fan" } })).data.user_ids;
  return (
    <div className={`flex flex-wrap items-center gap-5 text-sm ${className}`} data-testid="fan-follow-counts">
      <PeopleCountLink icon={<Rss className="h-4 w-4 text-primary" />} count={followerCount}
        singular="follower" plural="followers" title={`${who} followers`} loadIds={followerIds}
        testId="follow-count" wordTestId="open-followers-modal" />
      <PeopleCountLink icon={<Heart className="h-4 w-4 text-primary" />} count={fanCount}
        singular="fan" plural="fans" title={`${who} fans`} loadIds={fanIds}
        testId="fan-count" wordTestId="open-fans-modal" />
    </div>
  );
}

/** A counter word (e.g. "friends") that opens a {@link PeopleListModal} when count > 0.
 *  Renders icon + number + the word; the word is a link when there's a list to show. */
export function PeopleCountLink({ icon, count, singular, plural, title, loadIds, noun, nounPlural, testId, wordTestId }) {
  const [open, setOpen] = useState(false);
  const word = count === 1 ? singular : plural;
  return (
    <span className="inline-flex items-center gap-1.5" title={plural} aria-label={`${count} ${word}`} data-testid={testId}>
      {icon}
      <span className="font-[640] text-foreground">{count}</span>
      {count > 0 ? (
        <button type="button" onClick={() => setOpen(true)} className="text-muted-foreground transition-colors hover:text-primary hover:underline" data-testid={wordTestId}>{word}</button>
      ) : (
        <span className="text-muted-foreground" data-testid={wordTestId}>{word}</span>
      )}
      {count > 0 && open && (
        <PeopleListModal open={open} onOpenChange={setOpen} title={title} count={count} loadIds={loadIds} noun={noun || singular} nounPlural={nounPlural || plural} />
      )}
    </span>
  );
}
